import React, { cloneElement, Children, forwardRef } from 'react'
import useControlled from '../../utils/useControlled'
import PropTypes from 'prop-types'
import clsx from 'clsx'

import './stepper.scss'

const Stepper = forwardRef(function Stepper(props, ref) {
	const {
		activeStep: activeStepProp,
		children,
		className,
		nonLinear = false,
		onStepClick,
		showOnlyCurrentStep = false,
		small = false,
		startFromZero = false,
		...other
	} = props

	const [activeStep] = useControlled({
		controlled: activeStepProp,
		default: 0
	})

	const childrenArray = Children.toArray(children)
	const steps = childrenArray.map((step, index) => {
		const state = {
			index,
			active: false,
			completed: false,
			disabled: false
		}

		if (activeStep === index) {
			state.active = true
		} else if (!nonLinear && activeStep > index) {
			state.completed = true
		} else if (!nonLinear && activeStep < index) {
			state.disabled = true
		}

		return cloneElement(step, {
			last: index + 1 === childrenArray.length,
			onClick: onStepClick ? onStepClick(index) : null,
			small,
			showOnlyCurrentStep,
			startFromZero,
			...state,
			...step.props
		})
	})

	return (
		<div className={clsx(className, 'Vb-stepper')} ref={ref} {...other}>
			{steps}
		</div>
	)
})

export default Stepper

Stepper.propTypes = {
	/**
	 * Set the active step (zero based index).
	 * Set to -1 to disable all the steps.
	 * @default 0
	 */
	activeStep: PropTypes.number,
	/**
	 * Two or more `<StepperItem />` components.
	 */
	children: PropTypes.node,
	/**
	 * @ignore
	 */
	className: PropTypes.string,
	/**
	 * If set the `Stepper` will not assist in controlling steps for linear flow.
	 * @default false
	 */
	nonLinear: PropTypes.bool,
	/**
	 * Funtion to execute on step clicking.
	 * The index of the step is passed to the function on StepperItem evaluation.
	 */
	onStepClick: PropTypes.func,
	/**
	 * Hides non active steps from displaying
	 * @default false
	 */
	showOnlyCurrentStep: PropTypes.bool,
	/**
	 * Smaller font and indicators size.
	 * @default false
	 */
	small: PropTypes.bool,
	/**
	 * If step numeration is wanted to start on 0.
	 * @default false
	 */
	startFromZero: PropTypes.bool

}
